import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    ToggleButtonGroup,
    ToggleButton,
    Autocomplete,
    TextField,
    Popper,
} from '@mui/material';
import { styled } from '@mui/system';
import { useTranslation } from 'react-i18next';
import CustomMonthDatePicker from '../../Widgets/CustomDates/CustomMonthDatePicker/CustomMonthDatePicker';
import FixedSizeVirtualizedListbox from '../../Widgets/AutocompleteCardSelect/FixedSizeVirtualizedListbox';
import fieldStyles from '../../JournalEntryies/SubComponents/AddJournalEntryModal.module.scss';
import paStyles from './PaymentAcceptanceCreateEditModal.module.scss';

const StyledListItem = styled('li')({
    color: '#000000',
    '&:hover': {
        cursor: 'pointer',
    },
});

const ComboPopper = React.forwardRef(({ direction = 'rtl', ...props }, ref) => {
    const { className, style, modifiers, ...other } = props;
    return (
        <Popper
            {...other}
            ref={ref}
            dir={direction}
            className={`${className || ''} ${fieldStyles.comboPopper} edit-modal-combo-popper ${paStyles.comboMenuBlack}`}
            placement={direction === 'rtl' ? 'bottom-end' : 'bottom-start'}
            modifiers={[
                ...(modifiers || []),
                { name: 'offset', options: { offset: [0, 8] } },
            ]}
            style={{ ...style, zIndex: 2100, minWidth: style?.minWidth || 280 }}
        />
    );
});
ComboPopper.displayName = 'ComboPopper';

const UploadDocumentDialog = ({
    open,
    onClose,
    documents,
    chooseUploadDocument,
    setChooseUploadDocument,
    handleDocumentUpload,
    onContinue,
    direction,
    sortDate,
    title = 'UploadDocuments',
    primaryColor = '#304FFF',
    onMonthDatesChange,
    onResetDocuments,
    hideMonthPickers = false, // Hide month pickers for receipt/invoice receipt uploads
    showProjectSelector = false,
    segmentationProjects = [],
    selectedSegmentation = null,
    onSegmentationChange,
}) => {
    const { t } = useTranslation();
    const [reportingMonthDate, setReportingMonthDate] = useState(sortDate ? new Date(sortDate) : new Date());
    const [registrationMonthDate, setRegistrationMonthDate] = useState(sortDate ? new Date(sortDate) : new Date());
    const textAlign = direction === 'rtl' ? 'left' : 'right';

    const projectAutocompleteOptions = useMemo(
        () =>
            (segmentationProjects || []).map((p) => {
                const id = String(p.id ?? p.projectId ?? p.Id ?? '');
                const code = p.code ?? p.Code ?? '';
                const name = p.name ?? p.Name ?? '';
                const label =
                    `${code} — ${name}`.replace(/^ — | — $/g, '').trim() ||
                    p.label ||
                    id;
                return {
                    ...p,
                    id,
                    code,
                    name,
                    label,
                    kind: 'project',
                };
            }),
        [segmentationProjects]
    );

    const selectedProjectAutocompleteValue = useMemo(() => {
        if (!selectedSegmentation?.id) return null;
        const matched = projectAutocompleteOptions.find(
            (o) => String(o.id) === String(selectedSegmentation.id)
        );
        if (matched) return matched;
        return {
            ...selectedSegmentation,
            id: String(selectedSegmentation.id),
            kind: 'project',
            label:
                selectedSegmentation.label ||
                `${selectedSegmentation.code ?? ''} — ${selectedSegmentation.name ?? ''}`
                    .replace(/^ — | — $/g, '')
                    .trim() ||
                String(selectedSegmentation.id),
        };
    }, [selectedSegmentation, projectAutocompleteOptions]);

    const handleProjectAutocompleteChange = (_event, newValue) => {
        if (!onSegmentationChange) return;
        if (!newValue || newValue.kind === 'none' || newValue.id === '') {
            onSegmentationChange(null);
            return;
        }
        onSegmentationChange(newValue);
    };

    const renderComboPopper = useCallback(
        (props) => <ComboPopper {...props} direction={direction} />,
        [direction]
    );

    const inputPropsStyle = {
        disableUnderline: true,
    };

    const inputStyle = {
        style: { textAlign },
    };

    // Reset state when modal opens
    useEffect(() => {
        if (open) {
            const currentDate = sortDate ? new Date(sortDate) : new Date();
            setReportingMonthDate(currentDate);
            setRegistrationMonthDate(currentDate);
            
            // Reset documents if callback provided
            if (onResetDocuments) {
                onResetDocuments();
            }
            
            // Notify parent of date reset
            if (onMonthDatesChange) {
                onMonthDatesChange({ reportingMonthDate: currentDate, registrationMonthDate: currentDate });
            }
        }
    }, [open, sortDate]);

    const handleReportingMonthChange = (date) => {
        if (date) {
            setReportingMonthDate(date);
            if (onMonthDatesChange) {
                onMonthDatesChange({ reportingMonthDate: date, registrationMonthDate });
            }
        }
    };

    const handleRegistrationMonthChange = (date) => {
        if (date) {
            setRegistrationMonthDate(date);
            if (onMonthDatesChange) {
                onMonthDatesChange({ reportingMonthDate, registrationMonthDate: date });
            }
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    padding: 2
                }
            }}
        >
            <DialogTitle sx={{ pb: 1, pt: 2, textAlign }} dir={direction}>
                <Typography
                    variant="h6"
                    dir={direction}
                    sx={{ fontWeight: 600, color: primaryColor, textAlign, width: '100%' }}
                >
                    {t(title)}
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ pt: 2 }} dir={direction}>
                <ToggleButtonGroup
                    value={chooseUploadDocument}
                    exclusive
                    onChange={(e, val) => val !== null && setChooseUploadDocument(val)}
                    fullWidth
                    sx={{ mb: 2 }}
                    dir={direction}
                >
                    <ToggleButton value={true} sx={{
                        '&.Mui-selected': {
                            backgroundColor: primaryColor,
                            color: 'white',
                            '&:hover': { backgroundColor: primaryColor }
                        }
                    }}>
                        {t('WithDocuments')}
                    </ToggleButton>
                    <ToggleButton value={false} sx={{
                        '&.Mui-selected': {
                            backgroundColor: primaryColor,
                            color: 'white',
                            '&:hover': { backgroundColor: primaryColor }
                        }
                    }}>
                        {t('NoDocuments')}
                    </ToggleButton>
                </ToggleButtonGroup>

                {chooseUploadDocument && (
                    <>
                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                sx={{
                                    py: 3,
                                    borderColor: documents?.length > 0 ? '#28a745' : primaryColor,
                                    backgroundColor: documents?.length > 0 ? '#f0f8f4' : primaryColor === '#304FFF' ? '#f8f9ff' : '#fff8f0',
                                    borderStyle: 'dashed',
                                    borderWidth: 2,
                                    '&:hover': {
                                        borderColor: documents?.length > 0 ? '#218838' : primaryColor,
                                        backgroundColor: documents?.length > 0 ? '#e6f4ea' : primaryColor === '#304FFF' ? '#e8ecff' : '#fff5e6',
                                    }
                                }}
                            >
                                <Box sx={{ textAlign: 'center' }}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill={documents?.length > 0 ? "#28a745" : primaryColor}>
                                        <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z" />
                                    </svg>
                                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 500, color: documents?.length > 0 ? '#28a745' : primaryColor }}>
                                        {documents?.length > 0
                                            ? `${documents.length} ${t('FilesSelected')}`
                                            : t('ClickToUploadFiles')
                                        }
                                    </Typography>
                                    {documents?.length > 0 && (
                                        <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5, maxHeight: '60px', overflow: 'auto' }}>
                                            {documents.map(d => d.name).join(', ')}
                                        </Typography>
                                    )}
                                </Box>
                                <input
                                    type="file"
                                    hidden
                                    multiple
                                    accept="image/*,application/pdf"
                                    onChange={handleDocumentUpload}
                                />
                            </Button>
                        </Box>

                        {showProjectSelector && (
                            <div
                                className={direction === 'rtl' ? fieldStyles.rtl : fieldStyles.ltr}
                                dir={direction}
                                style={{ marginTop: 24 }}
                            >
                                <div style={{ fontWeight: 'bold', marginBottom: '3px', fontSize: '13px',  }}>
                                    {t('Projects')}
                                </div>
                                <Autocomplete
                                    className={fieldStyles.combo}
                                    value={selectedProjectAutocompleteValue}
                                    onChange={handleProjectAutocompleteChange}
                                    options={projectAutocompleteOptions}
                                    getOptionLabel={(option) => option?.label ?? ''}
                                    isOptionEqualToValue={(a, b) => {
                                        if (!a && !b) return true;
                                        if (!a || !b) return false;
                                        return String(a.id) === String(b.id);
                                    }}
                                    filterOptions={(options, state) => {
                                        const q = state.inputValue.trim().toLowerCase();
                                        if (!q) return options;
                                        return options.filter((o) => o.label.toLowerCase().includes(q));
                                    }}
                                    disableListWrap
                                    ListboxComponent={FixedSizeVirtualizedListbox}
                                    ListboxProps={{ direction, itemHeight: 40, listHeight: 240 }}
                                    PopperComponent={renderComboPopper}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            className={fieldStyles.styleInput}
                                            placeholder={t('Projects')}
                                            variant="standard"
                                            InputProps={{
                                                ...params.InputProps,
                                                ...inputPropsStyle,
                                            }}
                                            inputProps={{
                                                ...params.inputProps,
                                            }}
                                        />
                                    )}
                                    renderOption={(props, option) => (
                                        <StyledListItem
                                            {...props}
                                            key={props.key}
                                            style={{
                                                ...(props.style || {}),
                                                paddingTop: 10,
                                                paddingBottom: 8,
                                                paddingInline: 12,
                                                minHeight: 40,
                                                boxSizing: 'border-box',
                                            }}
                                        >
                                            {option.label}
                                        </StyledListItem>
                                    )}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            backgroundColor: '#f5f5f6',
                                        },
                                    }}
                                />
                            </div>
                        )}

                        {/* Month Date Pickers - Hidden for receipt/invoice receipt uploads */}
                        {!hideMonthPickers && (
                            <Box sx={{ mt: 3, display: 'flex', gap: 2 }} dir={direction}>
                                <Box sx={{ flex: 1, textAlign }} dir={direction}>
                                    <Typography
                                        variant="body2"
                                        dir={direction}
                                        sx={{ mb: 1, fontWeight: 500, color: '#666', textAlign, width: '100%' }}
                                    >
                                        {t('MonthReportDate')}
                                    </Typography>
                                    <CustomMonthDatePicker
                                        onDateChange={handleReportingMonthChange}
                                        date={reportingMonthDate}
                                        height="48px"
                                        direction={direction}
                                    />
                                </Box>
                                <Box sx={{ flex: 1, textAlign }} dir={direction}>
                                    <Typography
                                        variant="body2"
                                        dir={direction}
                                        sx={{ mb: 1, fontWeight: 500, color: '#666', textAlign, width: '100%' }}
                                    >
                                        {t('RegistrationMonthDate')}
                                    </Typography>
                                    <CustomMonthDatePicker
                                        onDateChange={handleRegistrationMonthChange}
                                        date={registrationMonthDate}
                                        height="48px"
                                        direction={direction}
                                    />
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }} dir={direction}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    sx={{
                        borderColor: '#e0e0e0',
                        color: '#666',
                        '&:hover': {
                            borderColor: '#bdbdbd',
                            backgroundColor: '#f5f5f5'
                        }
                    }}
                >
                    {t('close')}
                </Button>
                <Button
                    variant="contained"
                    disabled={chooseUploadDocument && !(documents.length > 0)}
                    onClick={() => onContinue(reportingMonthDate, registrationMonthDate)}
                    sx={{
                        backgroundColor: primaryColor,
                        '&:hover': { backgroundColor: primaryColor },
                        '&:disabled': {
                            backgroundColor: '#e0e0e0',
                            color: '#bdbdbd'
                        }
                    }}
                >
                    {t('Continue')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default UploadDocumentDialog;
